import { useMutation, useQueryClient } from '@tanstack/react-query';

import { karmaQueryKeys } from '@query';

import { rewardPoolBuyEntry } from '@shared/api/rewardPools';
import { RewardPool, RewardPoolTicketCurrencyType } from '@shared/api/types/rewardPools';
import { useAuth } from '@shared/common/providers/AuthProvider';
import {
  getSelfRewardPoolLeaderboardDataQueryOptions,
  rewardPoolsQueryKeys,
} from '@shared/hooks/query/rewardPools';

import { useIsJoinedToPool, useJoinRewardPool } from '@store:web/components/entities/rewardPool';
import useCheckRequiredSelfData from '@store:web/utils/hooks/useCheckRequiredSelfData';

import { incrementPoolCounter } from '../incrementPoolCounter';
import { useSignatureProps } from '../useSignatureProps';
import { getSignMessage } from './getSignMessage';

type Props = {
  rewardPool: RewardPool;
};

type BuyEntryProps = {
  entryAmount: number;
  currencyType: RewardPoolTicketCurrencyType;
};

export const useBuyEntry = ({ rewardPool }: Props) => {
  const { mutateAsync: joinRewardPool } = useJoinRewardPool(rewardPool.id);
  const { check } = useCheckRequiredSelfData();
  const queryClient = useQueryClient();
  const { getSignProps } = useSignatureProps();
  const { isAuth } = useAuth();
  const { isJoined, isLoading } = useIsJoinedToPool({ rewardPool });

  return useMutation({
    mutationFn: async ({ currencyType, entryAmount }: BuyEntryProps) => {
      if (isLoading) {
        return;
      }

      const signMessage = getSignMessage({ rewardPool, currencyType, entryAmount });

      if (isJoined) {
        const signProps = await getSignProps(signMessage);

        await rewardPoolBuyEntry({
          rewardPoolId: rewardPool.id,
          entryCurrency: currencyType.currency,
          entryAmount,
          ...signProps,
        });
        return entryAmount;
      }

      if (
        !check({
          auth: true,
          evmWallet: true,
        })
      ) {
        return 0;
      }

      await joinRewardPool({
        signMessage,
        rewardPool,
        entryCurrency: currencyType.currency,
        entryAmount,
      });
      return entryAmount;
    },
    onSuccess: (entryAmount) => {
      if (entryAmount) {
        void incrementPoolCounter({
          poolId: rewardPool.id,
          key: 'entries_counter',
          increment: entryAmount,
          queryClient,
        });
      }

      void queryClient.invalidateQueries({
        queryKey: rewardPoolsQueryKeys.rewardPool(rewardPool.id),
      });
      void queryClient.invalidateQueries({
        queryKey: karmaQueryKeys.userBalances(rewardPool.id),
      });
      void queryClient.invalidateQueries({
        queryKey: rewardPoolsQueryKeys.selfRewardPoolEntries(rewardPool.id, isAuth),
      });
      void queryClient.invalidateQueries({
        queryKey: rewardPoolsQueryKeys.rewardPoolLeaderboardInfinite(rewardPool.id),
      });
      void queryClient.invalidateQueries({
        queryKey: getSelfRewardPoolLeaderboardDataQueryOptions(rewardPool.id).queryKey,
      });
    },
  });
};
